"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeStringToBoolean = exports.getUniqueElements = exports.parseAutoGeneratedComment = exports.formatCurrencyNumber = void 0;
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const numeral_1 = __importDefault(require("numeral"));
/**
 * To be able to use numeral.locale("de")
 *
 * See: https://github.com/adamwdraper/Numeral-js/issues/548
 *
 * TODO: replace numberaljs - project is most likely abandonned
 */
require("numeral/locales/de");
const consts_1 = require("../consts");
const common_1 = require("../consts/common");
const models_1 = require("../models");
const autoGeneratedComments_enum_1 = require("../models/comment/autoGeneratedComments.enum");
const userSettings_interface_1 = require("../models/user/userSettings.interface");
const formatCurrencyNumber = ({ value, currency, showFullNumber = false, maxDigits = 8, localeData, showCurrencySymbol = true, }) => {
    var _a;
    // convert between locale if necessary
    if (localeData && localeData.localeFrom !== localeData.localeTo) {
        numeral_1.default.locale(localeData.localeFrom);
        const numeralValue = (0, numeral_1.default)(value);
        numeral_1.default.locale(localeData.localeTo);
        value = numeralValue.value();
    }
    if (!currency) {
        return (0, numeral_1.default)(value).format("00");
    }
    // To avoid too much digit count on number such as 21.00000003
    const testString = (0, numeral_1.default)(value).format("00");
    const decimalPrecision = consts_1.Currencies[currency].decimal_digits;
    const decimalFormatter = "0".repeat(decimalPrecision);
    const formatString = showFullNumber || testString.length <= maxDigits ? `0,0${decimalPrecision && `.${decimalFormatter}`}` : `0,0${decimalPrecision && `[.]${decimalFormatter}a`}`;
    const formattedValue = (0, numeral_1.default)(value).format(formatString);
    if (!showCurrencySymbol) {
        return formattedValue;
    }
    const prefix = currency === models_1.CurrencyEnum.USD ? "$ " : "";
    const suffix = currency !== models_1.CurrencyEnum.USD ? ` ${(_a = consts_1.Currencies[currency]) === null || _a === void 0 ? void 0 : _a.symbol}` : "";
    return `${prefix}${formattedValue}${suffix}`;
};
exports.formatCurrencyNumber = formatCurrencyNumber;
/**
 *  For auto generated comments, the comment message saved in the db has the following structure: "message,extra". Extra can be a string or a link.
 *   The differentiation is done in the locales json file.
 *   e.g
 *   - to interpolate a string: "has sent out a reminder to {{extra1}} (Approver)."
 *   - to interpolate a link: "has matched the request to <0>this transaction</0>."
 *       <0> -> 0 is the index of the component in the components array in <Trans ... />
 * @param message
 * @param userLanguage
 * @param otherParams
 * @returns
 */
const parseAutoGeneratedComment = (message, userLanguage, otherParams = {}) => {
    // Split array by commas, but not commas inside quotes (""). If you need to send a string with commas, surround it with quotes.
    const commentTokens = message.split(",");
    if (!(commentTokens === null || commentTokens === void 0 ? void 0 : commentTokens.length)) {
        return { commentTokens: [], commentMessage: "", commentExtras: [], commentLinks: [] };
    }
    let commentMessage = commentTokens[0]; // first "token" is the message
    let commentExtras = commentTokens.slice(1); // all the other "tokens", are the extras. (extra1, extra2... etc)
    const localeData = { localeFrom: userSettings_interface_1.UserLanguageEnum.EN, localeTo: userLanguage };
    const values = {};
    const commentLinks = [];
    // special cases
    if ([autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_REJECTION_NOTE, autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.INVOICE_UPLOADED_WITH_FILENAME, ...expenseTagsComments].includes(commentMessage)) {
        values.extra1 = `${commentExtras.join(",")}`;
    }
    else if (commentMessage === autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_GROSS_AMOUNT_CHANGED_DUE_TO_TRANSACTION ||
        commentMessage === autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_DISCOUNT_EDITED) {
        const oldAmount = commentExtras[0];
        const newAmount = commentExtras[1];
        const currency = commentExtras[2];
        // since comments are all stored in EN, we gotta convert the numbers if needed
        values.extra1 = (0, exports.formatCurrencyNumber)({ value: oldAmount, currency, showFullNumber: true, maxDigits: 8, localeData });
        values.extra2 = (0, exports.formatCurrencyNumber)({ value: newAmount, currency, showFullNumber: true, maxDigits: 8, localeData });
    }
    else if (approvedComments.includes(commentMessage) ||
        isRequestExportGobdComment(commentMessage) ||
        [autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_REQUEST_CREATED_WITH_PRICE, autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_TOTAL_GROSS_PRICE_UPDATED].includes(commentMessage) ||
        expenseDiscountComments.includes(commentMessage)) {
        const amount = commentExtras[0];
        const currency = commentExtras[1];
        const approvedForName = commentExtras[2];
        if (amount && currency) {
            values.extra1 = (0, exports.formatCurrencyNumber)({ value: Number(amount), currency, showFullNumber: true, maxDigits: 8, localeData });
        }
        if (approvedForName) {
            values.extra2 = approvedForName;
        }
    }
    else if (commentMessage === autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.INBOX_INVOICE_ATTACHED_WITH_FILENAME_AND_UPLOAD_DATE) {
        const invoiceName = commentExtras[0];
        const uploadDate = commentExtras[1];
        values.extra1 = invoiceName;
        values.extra2 = (0, moment_timezone_1.default)(uploadDate).format("DD.MM.YYYY");
    }
    else if (commentMessage === autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.AUTO_MATCH_INVOICE) {
        const invoiceName = commentExtras[0];
        values.extra1 = invoiceName;
    }
    else if (approvalProcessChangeComments.includes(commentMessage)) {
        const tokens = message.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
        if (tokens === null || tokens === void 0 ? void 0 : tokens.length) {
            commentMessage = tokens[0]; // first "token" is the message
            commentExtras = tokens.slice(1); // all the other "tokens", are the extras. (extra1, extra2... etc)
        }
        const approvalProcess1 = {
            name: commentExtras[0] === "undefined" ? undefined : commentExtras[0],
            id: commentExtras[1] === "undefined" ? undefined : commentExtras[1],
            isWorkflow: commentExtras[2] === "true",
        };
        const approvalProcess2 = {
            name: commentExtras[3] === "undefined" ? undefined : commentExtras[3],
            id: commentExtras[4] === "undefined" ? undefined : commentExtras[4],
            isWorkflow: commentExtras[5] === "true",
        };
        const singleApproverStr = "activity:autoGeneratedComments.approval_process.single_approver";
        const workflowStr = "activity:autoGeneratedComments.approval_process.workflow";
        values.extra1 = approvalProcess1.name ? approvalProcess1.name : "";
        values.extra2 = approvalProcess1.isWorkflow ? workflowStr : singleApproverStr;
        values.extra3 = approvalProcess2.name ? approvalProcess2.name : "";
        values.extra4 = approvalProcess2.isWorkflow ? workflowStr : singleApproverStr;
        const linkRoute1 = approvalProcess1.isWorkflow ? "workflows" : "employees";
        const linkRoute2 = approvalProcess2.isWorkflow ? "workflows" : "employees";
        // Right eval
        const hasRightsToReadResource = (route) => {
            if (!otherParams.rights) {
                return false;
            }
            if (route === "workflows") {
                return otherParams.rights.includes(models_1.RightEnum.WORKFLOW__ALL__READ);
            }
            if (route === "employees") {
                return otherParams.rights.includes(models_1.RightEnum.EMPLOYEE__ALL__READ);
            }
            return false;
        };
        const shouldAddLink1 = approvalProcess1.id && approvalProcess1.id !== otherParams.autoApproverId && hasRightsToReadResource(linkRoute1);
        const shouldAddLink2 = approvalProcess2.id && approvalProcess2.id !== otherParams.autoApproverId && hasRightsToReadResource(linkRoute2);
        commentLinks[0] = shouldAddLink1 ? `/${linkRoute1}/${approvalProcess1.id}` : null;
        commentLinks[1] = shouldAddLink2 ? `/${linkRoute2}/${approvalProcess2.id}` : null;
    }
    else if (approvalProcessInitializationComments.includes(commentMessage)) {
        const workflowName = commentExtras[0];
        const workflowId = commentExtras[1];
        values.extra1 = workflowName;
        commentLinks[0] = workflowId ? `/workflows/${workflowId}` : null;
    }
    else if (commentMessage.includes(autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.FOLDER_RESET)) {
        const folderNumber = commentExtras[0];
        const previousStatus = commentExtras[1];
        const newStatus = commentExtras[2];
        values.extra1 = folderNumber;
        values.extra2 = getStatusLabel(Number(previousStatus));
        values.extra3 = getStatusLabel(Number(newStatus));
    }
    else if (commentMessage.includes(autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_ATTACHED_TO_FOLDER)) {
        commentLinks[0] = commentExtras[0];
    }
    else if (commentMessage.includes(autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.AUTO_MATCH)) {
        commentLinks[0] = commentExtras[0];
    }
    else {
        for (let i = 0, len = commentExtras.length; i <= len; i++) {
            values[`extra${i + 1}`] = commentExtras[i];
        }
    }
    return { commentMessage, values, commentExtras, commentLinks };
};
exports.parseAutoGeneratedComment = parseAutoGeneratedComment;
const isRequestExportGobdComment = (commentMessage) => {
    const commonRequestExportCommentMessage = commentMessage.slice(0, commentMessage.lastIndexOf(common_1.DEFAULT_COMMENT_SPLIT_SYMBOL));
    return commonRequestExportCommentMessage === autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_EXPORTED_INFO_GOBD;
};
const getStatusLabel = (status, isDraft) => {
    if (isDraft) {
        return "label:draft";
    }
    switch (status) {
        case models_1.ExpenseStatusEnum.PURCHASE_PENDING:
            return "label:expense_status.purchase_pending";
        case models_1.ExpenseStatusEnum.DOCS_NEEDED:
            return "label:expense_status.docs_needed";
        case models_1.ExpenseStatusEnum.INVOICE_PENDING:
            return "label:expense_status.invoice_pending";
        case models_1.ExpenseStatusEnum.APPROVED:
            return "label:expense_status.approved";
        case models_1.ExpenseStatusEnum.DECLINED:
            return "label:expense_status.declined";
        case models_1.ExpenseStatusEnum.REVIEWED:
            return "label:expense_status.reviewed";
        case models_1.ExpenseStatusEnum.PAID:
            return "label:expense_status.paid";
        case models_1.ExpenseStatusEnum.DONE:
            return "label:expense_status.done";
        default:
            return "n/a";
    }
};
const approvalProcessChangeComments = [
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_PURCHASE_CHANGE_DUE_TO_WORKFLOW_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_PURCHASE_CHANGE_DUE_TO_WORKFLOW_DELETION,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_PURCHASE_CHANGE_DUE_TO_EXPENSE_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_INVOICE_CHANGE_DUE_TO_WORKFLOW_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_INVOICE_CHANGE_DUE_TO_WORKFLOW_DELETION,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_INVOICE_CHANGE_DUE_TO_EXPENSE_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_FOLDER_INVOICE_CHANGE_DUE_TO_WORKFLOW_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_FOLDER_INVOICE_CHANGE_DUE_TO_WORKFLOW_DELETION,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_FOLDER_INVOICE_CHANGE_DUE_TO_EXPENSE_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_PURCHASE_ORDER_CHANGE_DUE_TO_WORKFLOW_UPDATE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_PURCHASE_ORDER_CHANGE_DUE_TO_WORKFLOW_DELETION,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_PURCHASE_ORDER_CHANGE_DUE_TO_PURCHASE_ORDER_UPDATE,
];
const approvalProcessInitializationComments = [
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_PURCHASE_INITIALIZATION_WORKFLOW,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_EXPENSE_INVOICE_INITIALIZATION_WORKFLOW,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_FOLDER_INVOICE_INITIALIZATION_WORKFLOW,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.APPROVAL_PROCESS_PURCHASE_ORDER_INITIALIZATION_WORKFLOW,
];
const approvedComments = [
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_REQUEST_APPROVED_WITH_PRICE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_REQUEST_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED_WITH_PRICE,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.FOLDER_REQUEST_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.FOLDER_REQUEST_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.MILEAGE_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.MILEAGE_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.HOSPITALITY_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.HOSPITALITY_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PER_DIEM_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PER_DIEM_APPROVED_FOR,
    // old comments (replaced by WITH_PRICE options)
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_REQUEST_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_SUBMITTED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_SUBMITTED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_APPROVED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_APPROVED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_REVIEWED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_REVIEWED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_CONFIRMED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_CONFIRMED_FOR,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_REJECTED,
    autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.PURCHASE_ORDER_REJECTED_FOR,
];
const expenseTagsComments = [autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_TAG_ATTACHED, autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_TAG_DETACHED];
const expenseDiscountComments = [autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_DISCOUNT_ADDED, autoGeneratedComments_enum_1.AutoGeneratedCommentsEnum.REQUEST_DISCOUNT_REMOVED];
const getUniqueElements = (arr, keyToCheck) => [...new Map(arr.map((el) => [el[keyToCheck], el])).values()];
exports.getUniqueElements = getUniqueElements;
const normalizeStringToBoolean = (value) => {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string" && (value === null || value === void 0 ? void 0 : value.toLowerCase()) === "true") {
        return true;
    }
    if (typeof value === "string" && (value === null || value === void 0 ? void 0 : value.toLowerCase()) === "false") {
        return false;
    }
    return undefined;
};
exports.normalizeStringToBoolean = normalizeStringToBoolean;
